import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, SectionTag } from "../components/Reveal";
import SEO from "../components/SEO";
import { SITE } from "../content";

export default function About() {
  return (
    <div data-testid="about-page">
      <SEO
        title="About Nitin Gupta & The Next Design, Strategy-Led Creative Partner for Manufacturers"
        description="Nitin Gupta, founder of The Next Design, 20+ years solving business problems for manufacturers using strategy, technology and design."
      />
      <section className="border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <SectionTag>About</SectionTag>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-light uppercase tracking-tight leading-[0.95]" data-testid="about-heading">
                Led by strategy.<br /><span className="text-brand-red">Driven by outcomes.</span>
              </h1>
              <p className="mt-8 text-lg text-neutral-600 max-w-xl leading-relaxed">
                The Next Design is a founder-led creative studio for manufacturers. {SITE.differentiator}
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <div className="img-zoom border border-neutral-900 bg-neutral-950" data-testid="about-founder-image">
                <img src={`${process.env.PUBLIC_URL}/assets/nitin-gupta-portrait.png`} alt="Nitin Gupta, Founder, The Next Design" className="w-full object-cover" />
              </div>
              <p className="mt-4 text-xs uppercase tracking-[0.25em] text-neutral-500">Nitin Gupta, Founder & Strategic Lead</p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Reveal><SectionTag>The Founder</SectionTag></Reveal>
          </div>
          <div className="lg:col-span-8">
            <Reveal>
              <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-tight leading-tight" data-testid="about-founder-heading">
                Nitin Gupta
              </h2>
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8 text-neutral-600 leading-relaxed">
                <p>
                  For over two decades, Nitin has worked at the intersection of business strategy, technology and design, helping manufacturers turn operational strength into market perception.
                </p>
                <p>
                  The team executes with precision across branding, 3D, AR, exhibitions and digital. But the strategic direction of every engagement, what to say, to whom, and why, is led by Nitin personally.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-b border-neutral-200 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <SectionTag>How We Think</SectionTag>
            <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-tight max-w-3xl" data-testid="about-principles-heading">
              Three principles, unchanged for 20 years.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-neutral-200 border border-neutral-200">
            {[
              { n: "01", t: "Problem before medium", d: "We start with the business problem, not the deliverable. The right answer might be a render, a stall, or a rebrand, the diagnosis decides." },
              { n: "02", t: "Proof over claims", d: "Buyers believe what they can see. We build visual proof, of product quality, of factory credibility, and let it carry the argument." },
              { n: "03", t: "Outcomes over output", d: "Work is measured by what it does for the business: better buyers, shorter sales cycles, stronger mandates. Not by how much of it there is." },
            ].map((p, i) => (
              <Reveal key={p.n} delay={i * 0.08} className="bg-white">
                <div className="p-10 h-full" data-testid={`about-principle-${i}`}>
                  <span className="font-display text-5xl font-semibold text-brand-red">{p.n}</span>
                  <h3 className="mt-5 font-display text-2xl font-semibold tracking-tight">{p.t}</h3>
                  <p className="mt-4 text-neutral-600 leading-relaxed">{p.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-neutral-950 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-28 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-10">
          <Reveal>
            <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-tight" data-testid="about-cta-heading">
              Talk strategy with Nitin directly.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to="/contact"
              data-testid="about-cta-button"
              className="bg-brand-red hover:bg-brand-red-dark text-white px-8 py-4 font-bold uppercase tracking-widest text-sm transition-colors inline-flex items-center gap-2"
            >
              Start a Conversation <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
